import { useMetricsContext } from 'modules/productAnalytics';
import { useMemo } from 'react';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';
import { APPLY_BUTTON, IN_APP_REPORTING, InAppMetricsToBreakdown } from '../constants';
import {
  useAppliedFiltersContext,
  useCampaignAndMetricFiltersContext,
  useTimeFiltersContext,
} from '../context';
import { RestDataIdentifier, RestQueryParams, SelectedMetrics } from '../types';
import { getHighLevelAndMedMetricsFromMetrics } from '../utils';

/**
 * Custom hook to generate query parameters for REST requests based on the provided data identifier.
 *
 * @param {RestDataIdentifier} restDataIdentifier - The identifier for the type of data to fetch.
 * @returns {RestQueryParams} The generated query parameters.
 */
export const useRestQueryParams = (restDataIdentifier: RestDataIdentifier) => {
  const isApplyButtonEnabled = useFeatureFlag(APPLY_BUTTON);
  const isWebInAppReportingFeatureFlagOn = useFeatureFlag(IN_APP_REPORTING);

  // Time-based filters
  const { dateParams } = useTimeFiltersContext();

  // Applied filters
  const { appliedFilters } = useAppliedFiltersContext();

  // Campaign and metric filters
  const { selectedMediums, selectedCrossMetrics, campaignFilters } =
    useCampaignAndMetricFiltersContext();

  // Metrics
  const { highLevelMetrics, analyticsMetrics } = useMetricsContext();

  const { overallMetrics, mediumMetrics } = getHighLevelAndMedMetricsFromMetrics(
    selectedCrossMetrics,
    highLevelMetrics,
    analyticsMetrics,
  );

  const activeCampaignFilters = isApplyButtonEnabled
    ? {
        mediums: appliedFilters?.mediums,
        ...appliedFilters?.campaignFilters,
      }
    : {
        mediums: selectedMediums,
        ...campaignFilters,
      };

  const activeOverallMetrics = isApplyButtonEnabled
    ? appliedFilters?.overallMetrics
    : overallMetrics;

  const activeMediumMetrics = isApplyButtonEnabled
    ? [...(appliedFilters?.mediumMetrics || [])]
    : [...mediumMetrics];

  const inAppBreakdownMetrics = selectedCrossMetrics.reduce((acc, curr) => {
    if (isWebInAppReportingFeatureFlagOn && InAppMetricsToBreakdown[curr]) {
      acc.push({ name: InAppMetricsToBreakdown[curr] });
    }
    return acc;
  }, [] as SelectedMetrics);

  const queryData = useMemo(() => {
    let data: RestQueryParams;
    switch (restDataIdentifier) {
      case RestDataIdentifier.TIME_SERIES:
      case RestDataIdentifier.OVERVIEW:
        data = {
          startTime: dateParams.startTime?.toISOString(),
          endTime: dateParams.endTime?.toISOString(),
          campaignFilters: activeCampaignFilters,
          metrics: {
            overallMetrics: activeOverallMetrics || [],
            mediumMetrics: [...activeMediumMetrics, ...inAppBreakdownMetrics],
          },
        };
        break;
      case RestDataIdentifier.DEVICE:
      case RestDataIdentifier.LOCATION:
        data = {
          startTime: dateParams.startTime?.toISOString(),
          endTime: dateParams.endTime?.toISOString(),
          campaignFilters: activeCampaignFilters,
          metrics: {
            overallMetrics: [],
            mediumMetrics: [
              {
                name: restDataIdentifier,
              },
            ],
          },
        };
        break;

      case RestDataIdentifier.CAMPAIGNS_FOR_METRICS:
        data = {
          startTime: dateParams.startTime?.toISOString(),
          endTime: dateParams.endTime?.toISOString(),
          campaignFiltersOpt: activeCampaignFilters,
        };
        break;

      case RestDataIdentifier.PDF_REPORT:
        data = {
          startTime: dateParams.startTime?.toISOString(),
          endTime: dateParams.endTime?.toISOString(),
          campaignFilters: activeCampaignFilters,
          metrics: {
            overallMetrics: activeOverallMetrics || [],
            mediumMetrics: activeMediumMetrics || [],
          },
        };
        break;

      default:
        data = {};
        break;
    }
    return data;
  }, [
    dateParams,
    activeCampaignFilters,
    activeOverallMetrics,
    activeMediumMetrics,
    restDataIdentifier,
    inAppBreakdownMetrics,
  ]);

  return queryData;
};
